import { useState, useCallback, useRef, useMemo } from "react";
import type { Map as MapboxMap } from "mapbox-gl";
import { AssignmentsPanel } from "../assignments/AssignmentsPanel";
import { MapView } from "../map/MapView";
import { MapContextProvider } from "../map/MapContext";
import { useMunicipalities } from "../api/map";
import { getBoundingBoxFromFeature } from "../api/map/utils";
import type { AssignmentArea } from "../api/types";
import type { SetHighlightedAssignmentAreaOptions } from "../map/MapContext";
import type { GeoJsonFeature, MunicipalityFeatureProperties } from "../api/map/types";
import "./admin-assignments-page.css";

export const AdminAssignmentsPage = (): JSX.Element => {
    const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string>("");
    const [selectedMunicipalityCode, setSelectedMunicipalityCode] = useState<string>("");

    const municipalityGeometryQuery = useMunicipalities(
        { codes: selectedMunicipalityCode || undefined, limit: 1, includeGeometry: true },
        null,
        { enabled: !!selectedMunicipalityCode }
    );

    const selectedMunicipalityFeature = useMemo((): GeoJsonFeature<MunicipalityFeatureProperties> | null => {
        const features = municipalityGeometryQuery.data?.features;
        if (!features?.length || !selectedMunicipalityId) return null;
        return (features.find(
            (f) => (f.id != null && String(f.id) === selectedMunicipalityId) || f.properties.municipalityPolygonId === selectedMunicipalityId
        ) ?? features[0]) as GeoJsonFeature<MunicipalityFeatureProperties>;
    }, [selectedMunicipalityId, municipalityGeometryQuery.data?.features]);

    const fitBounds = useMemo(() => {
        if (!selectedMunicipalityFeature) return null;
        return getBoundingBoxFromFeature(selectedMunicipalityFeature as GeoJsonFeature<unknown>) ?? null;
    }, [selectedMunicipalityFeature]);
    const [polygonArea, setPolygonArea] = useState<AssignmentArea | null>(null);
    const [highlightedAssignmentArea, setHighlightedAssignmentAreaState] = useState<AssignmentArea | null>(null);
    const [highlightedAssignmentId, setHighlightedAssignmentId] = useState<string | null>(null);
    const [assignmentHighlightNonce, setAssignmentHighlightNonce] = useState(0);
    const polygonCompleteCallbackRef = useRef<((area: AssignmentArea | null) => void) | undefined>();

    const setHighlightedAssignmentArea = useCallback(
        (area: AssignmentArea | null, options?: SetHighlightedAssignmentAreaOptions) => {
            setHighlightedAssignmentAreaState(area);
            if (!area) {
                setHighlightedAssignmentId(null);
            } else if (options?.assignmentId != null) {
                setHighlightedAssignmentId(options.assignmentId);
            } else {
                setHighlightedAssignmentId(null);
            }
            if (area) {
                setAssignmentHighlightNonce((n) => n + 1);
            }
        },
        []
    );

    const handleMapReady = useCallback((map: MapboxMap) => {
        setMapInstance(map);
        setIsMapLoaded(true);
    }, []);

    const handleStartPolygonDrawing = useCallback(
        (initialArea?: AssignmentArea, onComplete?: (area: AssignmentArea | null) => void) => {
            setHighlightedAssignmentAreaState(null);
            setHighlightedAssignmentId(null);
            setIsDrawingPolygon(true);
            setPolygonArea(initialArea || null);
            polygonCompleteCallbackRef.current = onComplete;
        },
        []
    );

    const handleStopPolygonDrawing = useCallback(() => {
        setIsDrawingPolygon(false);
        polygonCompleteCallbackRef.current = undefined;
    }, []);

    const handlePolygonAreaChange = useCallback((area: AssignmentArea | null) => {
        setPolygonArea(area);
        if (polygonCompleteCallbackRef.current) {
            polygonCompleteCallbackRef.current(area);
        }
    }, []);

    return (
        <div className={`admin-assignments-page ${isDrawingPolygon ? 'admin-assignments-page--drawing' : ''}`}>
            <MapContextProvider
                map={mapInstance}
                isMapLoaded={isMapLoaded}
                isDrawingPolygon={isDrawingPolygon}
                polygonArea={polygonArea}
                highlightedAssignmentArea={highlightedAssignmentArea}
                highlightedAssignmentId={highlightedAssignmentId}
                onHighlightedAssignmentAreaChange={setHighlightedAssignmentArea}
                assignmentHighlightNonce={assignmentHighlightNonce}
                onStartDrawing={handleStartPolygonDrawing}
                onStopDrawing={handleStopPolygonDrawing}
                onAreaChange={handlePolygonAreaChange}
            >
                <div className="admin-assignments-page__content">
                    <div className="admin-assignments-page__form">
                        <AssignmentsPanel onMunicipalityChange={(id, code) => { setSelectedMunicipalityId(id); setSelectedMunicipalityCode(code); }} />
                    </div>
                    <div className="admin-assignments-page__map">
                        <MapView
                            hideAllLayers={true}
                            municipalityId={selectedMunicipalityId || null}
                            showLegend={false}
                            showStatusIndicator={false}
                            showFilterPanel={false}
                            showStatusBar={false}
                            showDistributorPins={!!selectedMunicipalityId}
                            distributorPinsMunicipalityId={selectedMunicipalityId || null}
                            fitBounds={fitBounds}
                            selectedMunicipalityFeature={selectedMunicipalityFeature}
                            onMapReady={handleMapReady}
                        />
                    </div>
                </div>
            </MapContextProvider>
        </div>
    );
};